<template>
  <div class="verify-pass">
    <teleport to="body">
      <overlay
        key="discountFormOverlay"
        block-class="discount-form-overlay"
        :direction="isMobile ? 'up' : 'left'"
        namespace="discountFormOverlay"
        type="drawer"
      >
        <template #title>
          <span
            class="text-body-l-bold"
            v-html="liquid.discountTitle"
          />
        </template>

        <template #body>
          <div class="discount-form">
            <form ref="form">
              <div
                class="discount-form__description text-body-s-regular"
                v-html="liquid.discountFormDescription"
              />

              <div class="form-group">
                <div class="form-group__field">
                  <label
                    class="form-group__label visually-hidden"
                    for="first_name"
                    v-text="liquid.discountFormFirstName"
                  />

                  <input
                    id="first_name"
                    type="text"
                    name="first_name"
                    :placeholder="liquid.discountFormFirstName"
                    value=""
                    required
                  >
                </div>

                <div class="form-group__field">
                  <label
                    class="form-group__label visually-hidden"
                    for="last_name"
                    v-text="liquid.discountFormLastName"
                  />

                  <input
                    id="last_name"
                    type="text"
                    name="last_name"
                    :placeholder="liquid.discountFormLastName"
                    value=""
                    required
                  >
                </div>

                <div class="form-group__field">
                  <label
                    class="form-group__label visually-hidden"
                    for="email"
                    v-text="liquid.discountFormEmail"
                  />

                  <input
                    id="email"
                    type="email"
                    name="email"
                    :placeholder="liquid.discountFormEmail"
                    value=""
                    required
                  >
                </div>

                <div class="form-group__field">
                  <input
                    id="checkbox"
                    v-model="checkboxKlaviyoChecked"
                    class="checkbox-input__input visually-hidden"
                    type="checkbox"
                    name="checkbox"
                    value="1"
                  >

                  <label
                    class="
                      checkbox-input__label
                      checkbox-input__label--small
                      text-body-xs-regular
                    "
                    for="checkbox"
                    v-text="liquid.discountFormCheckboxText"
                  />
                </div>

                <btn
                  class="discount-form__button"
                  :label="liquid.discountFormCta"
                  modifiers="primary block center"
                  type="submit"
                />
              </div>
            </form>

            <div
              ref="successMessage"
              class="discount-form__success-message-container is-hidden"
            >
              <icon-check />

              <p
                class="discount-form__success-message text-heading-l"
                v-text="liquid.discountFormSuccessMessage"
              />

              <p
                class="
                  discount-form__success-message-addition
                  text-body-m-regular
                "
                v-text="liquid.discountFormSuccessMessageAddition"
              />
            </div>

            <div
              ref="failedMessage"
              class="discount-form__failed-message-container is-hidden"
            >
              <p
                class="discount-form__failed-message text-heading-l"
                v-text="liquid.discountFormFailedMessage"
              />
            </div>
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Verify pass (verify-pass)
 * -----------------------------------------------------------------------------
 * Verify pass.
 * - Limited interactivity section.
 *
 *  * @param {String} liquid.sectionId - Section's Liquid ID.
 *  * @param {String} liquid.discountTitle - Discount title.
 *  * @param {String} liquid.discountFormUrlQcontact - Discount form
 *    URL for Qcontact.
 *  * @param {String} liquid.discountFormDescription - Discount form
 *    description.
 *  * @param {String} liquid.discountFirstName - Discount first name.
 *  * @param {String} liquid.discountFormLastName - Discount form last name.
 *  * @param {String} liquid.discountFormEmail - Discount form email.
 *  * @param {String} liquid.discountFormCheckboxText - Discount form
 *    checkbox text.
 *  * @param {String} liquid.discountFormCta - Discount form cta.
 *  * @param {String} liquid.discountFormSuccessMessage - Discount form
 *    success message.
 *  * @param {String} liquid.discountFormSuccessMessageAddition - Discount form
 *    success message addition name.
 *  * @param {String} liquid.discountFormFailedMessage - Discount form
 *    failed message.
 *
 */
import { mapActions, mapState } from 'vuex'

import subscribe from '~/apis/klaviyo'

import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import IconCheck from '~icons/general/check.svg'

import classes from '~/config/classes'

export default {
  name: 'VerifyPass',

  components: {
    Btn,
    IconCheck,
    Overlay,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        discountTitle: '',
        discountFormUrlQcontact: '',
        discountFormDescription: '',
        discountFormFirstName: '',
        discountFormLastName: '',
        discountFormEmail: '',
        discountFormCheckboxText: '',
        discountFormCta: '',
        discountFormSuccessMessage: '',
        discountFormSuccessMessageAddition: '',
        discountFormFailedMessage: '',
      }),
    },
  },

  data() {
    return {
      nodeSelectors: false,
      checkboxKlaviyoChecked: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    this.setNodeSelectors()
    this.setEventListeners()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Set node selectors.
     */
    setNodeSelectors() {
      this.nodeSelectors = {
        getCodeButton: document.querySelector('[js-discount-form="getCodeButton"]'),
      }
    },

    /**
     * Set event listeners.
     */
    setEventListeners() {
      this.nodeSelectors.getCodeButton.addEventListener('click', () => {
        this.handleToggle()
      })

      this.$refs.form.addEventListener('submit', (event) => {
        this.handleForm(event)
      })
    },

    /**
     * Handle form submission.
     */
    async handleForm(event) {
      event.preventDefault()
      const form = this.$refs.form
      const formData = new FormData(form)

      const formObject = {}
      formData.forEach((value, key) => {
        formObject[key] = value
      })
      const json = JSON.stringify(formObject)

      try {
        const response = await fetch(`${this.liquid.discountFormUrlQcontact}`, {
          method: 'POST',
          body: json,
          headers: {
            'Content-Type': 'application/json',
          },
        })
        const data = await response.json()

        if (this.checkboxKlaviyoChecked) {
          this.handleKlaviyoCheckbox(formObject)
        }

        this.handleSuccessMessage()
        return data

      } catch (error) {
        this.handleFailedMessage()
        throw new Error('Failed', error)
      }
    },

    /**
     * Handle Klaviyo checkbox submission.
     */
    handleKlaviyoCheckbox(formObject) {
      const profile = {
        email: formObject.email,
        first_name: formObject.first_name,
        last_name: formObject.last_name,
      }

      subscribe('Discount Form', profile)
        .then((response) => {
          return response
        })
        .catch((error) => {
          reportError('Klaviyo subscription failed', error)
        })
    },

    /**
     * Handle success message.
     */
    handleSuccessMessage() {
      this.$refs.form.classList.add(classes.hidden)
      this.$refs.successMessage.classList.remove(classes.hidden)
    },

    /**
     * Handle failed message.
     */
    handleFailedMessage() {
      this.$refs.form.classList.add(classes.hidden)
      this.$refs.failedMessage.classList.remove(classes.hidden)
    },

    /**
     * Handle toggle.
     */
    handleToggle() {
      this.toggleOverlay({
        ignoreDismissed: true,
        namespace: 'discountFormOverlay',
      })
    },
  },
}
</script>

<style lang="scss">
@import './discount-form-overlay';
@import './discount-form';
@import './verify-pass';
@import '@/components/form-group';
@import '~async/utils/checkbox-input/checkbox-input';
</style>
